/**
 * Type definitions and constants for the InputEditor component
 *
 * This file contains all the TypeScript types, interfaces, and constants
 * used by the Slate-based rich text editor component.
 */

import React from 'react';
import type { BaseEditor, Descendant } from 'slate';
import type { ReactEditor } from 'slate-react';
import type { HistoryEditor } from 'slate-history';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Supported list types in the editor */
export const LIST_TYPES = ['numbered-list', 'bulleted-list'] as const;

/** Supported text alignment types */
export const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'] as const;

// ============================================================================
// BASIC TYPES
// ============================================================================

/** Union type for list elements */
export type ListType = (typeof LIST_TYPES)[number];

/** Union type for text alignment options */
export type AlignType = (typeof TEXT_ALIGN_TYPES)[number];

/** Union type for all block element formats */
export type CustomElementFormat =
  | 'paragraph'
  | 'heading'
  | ListType
  | AlignType;

// ============================================================================
// SLATE ELEMENT TYPES
// ============================================================================

/**
 * Custom element type for Slate editor
 *
 * Represents all possible block-level elements in the editor:
 * - paragraph: Standard paragraph text
 * - heading: Header text (h2 level)
 * - bulleted-list/numbered-list: List containers
 * - list-item: Individual list items
 * - link: Inline link elements
 */
export type CustomElement = {
  /** The type of element */
  type:
    | 'paragraph'
    | 'heading'
    | 'bulleted-list'
    | 'numbered-list'
    | 'list-item'
    | 'link';
  /** Text alignment (left, center, right, justify) */
  align?: AlignType;
  /** URL for link elements */
  url?: string;
  /** Indentation level (0-10) for nested content */
  indent?: number;
  /** Child elements (can be nested elements or text) */
  children: (CustomElement | CustomText)[];
};

/**
 * Custom text type for Slate editor
 *
 * Represents inline text content with optional formatting:
 * - text: The actual text content
 * - bold: Whether text is bold
 * - italic: Whether text is italic
 */
export type CustomText = {
  /** The text content */
  text: string;
  /** Whether the text is bold */
  bold?: true;
  /** Whether the text is italic */
  italic?: true;
};

// ============================================================================
// SLATE MODULE AUGMENTATION
// ============================================================================

/**
 * Extend Slate's type system with our custom types
 * This allows TypeScript to provide proper type checking and autocomplete
 */
declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

// ============================================================================
// COMPONENT INTERFACES
// ============================================================================

/**
 * Props for the InputEditor component
 */
export interface InputEditorProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Initial content (HTML string or Slate nodes) */
  value?: string | Descendant[];
  /** Callback when content changes (returns HTML string) */
  onChange?: (value: string) => void;
  /** Placeholder text for empty editor */
  placeholder?: string;
  /** Whether to focus editor on mount */
  autoFocus?: boolean;
  /** Minimum height of editor content area (px) */
  minHeight?: number;
  /** Maximum height of editor content area (px) */
  maxHeight?: number;
  /** Whether to display formatting toolbar */
  showToolbar?: boolean;
  /** Visual theme for the editor */
  theme?: 'default' | 'light' | 'dark';
  /** Error message to display */
  error?: string;
  /** Whether editor is read-only */
  disabled?: boolean;
  /** Formik form instance for integration */
  formik?: any;
  /** Formik field configuration */
  field?: any;

  // Legacy props for backward compatibility
  /** Form field name (used with Formik) */
  name?: string;
}

/**
 * Props for toolbar button components
 */
export interface ToolbarButtonProps {
  /** Whether the button represents an active state */
  active?: boolean;
  /** Click handler for the button */
  onMouseDown: (event: React.MouseEvent) => void;
  /** Icon component to display */
  children: React.ReactNode;
  /** Optional disabled state */
  disabled?: boolean;
}

/**
 * Props for menu-based toolbar buttons
 */
export interface MenuButtonProps {
  /** Icon to display on the button */
  icon: React.ReactNode;
  /** Tooltip/aria-label for accessibility */
  label: string;
  /** Menu items to display in dropdown */
  items: Array<{
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    active?: boolean;
  }>;
}
